/**
 * Apply functions for HTML Stepper updates.
 * Uses shared HTML element utilities for consistent behavior with WebsiteUpdater.
 */
import {
  HtmlStepperFormData,
  HtmlStepperStep,
  HtmlStepperUpdate,
  HtmlStepperUpdateTarget,
  StepperActionElementData,
} from '@superfunnel/types';
import { extractFieldsFromHtmlBrowser } from '@superfunnel/utils';
import {
  applyAssetSettingsToHtml,
  applyHtmlElementChangeToHtml,
  applySelectAssetToHtml,
  applySelectThumbnailToHtml,
  AssetSettingsContent,
  getHtmlFromDoc,
  HtmlUpdateResult,
  parseHtmlAndFindElement,
  SelectAssetData,
  SelectThumbnailData,
} from '../../../utils/html-element-utils';

// ============================================================================
// Field Extraction
// ============================================================================

/**
 * Re-extract fields from a step's HTML and update the step's fields array.
 * Called after any HTML modification to keep fields in sync.
 */
function syncStepFields(step: HtmlStepperStep): void {
  if (!step.html) {
    step.fields = [];
    return;
  }

  try {
    step.fields = extractFieldsFromHtmlBrowser(step.html);
    // console.log('[STEPPER_UPDATER] Fields synced:', step.fields.length);
  } catch (error) {
    console.error('[STEPPER_UPDATER] Error extracting fields:', error);
    // Keep existing fields on error
  }
}

// ============================================================================
// Stepper-specific HTML Action Application
// ============================================================================

/**
 * Apply stepper action changes to an element.
 * Uses data-sf-action for navigation and href/target for links.
 *
 * For navigation actions (next, back, goto:stepId): Sets data-sf-action
 * For link action: Sets href and target, removes data-sf-action
 */
function applyStepperActionToHtml(
  html: string,
  elementPath: string,
  content: StepperActionElementData,
): HtmlUpdateResult {
  const result = parseHtmlAndFindElement(html, elementPath);
  if (!result) {
    return { success: false, error: `Element not found: ${elementPath}` };
  }

  const { doc, element } = result;
  const { action, url, openInNewTab } = content;
  const currentTagName = element.tagName.toLowerCase();
  let finalElement: Element = element;

  const isLinkAction = action === 'link';

  if (isLinkAction && url) {
    // Link action: Convert to anchor if needed, set href/target
    if (currentTagName === 'button') {
      const newAnchor = doc.createElement('a');
      Array.from(element.attributes).forEach((attr) => {
        newAnchor.setAttribute(attr.name, attr.value);
      });
      newAnchor.innerHTML = element.innerHTML;
      element.parentNode?.replaceChild(newAnchor, element);
      finalElement = newAnchor;
    }

    // Set link attributes
    finalElement.setAttribute('href', url);
    if (openInNewTab) {
      finalElement.setAttribute('target', '_blank');
    } else {
      finalElement.removeAttribute('target');
    }
    // Remove navigation attribute
    finalElement.removeAttribute('data-sf-action');
  } else {
    // Navigation action: Convert to button if needed, set data-sf-action
    if (currentTagName === 'a') {
      const newButton = doc.createElement('button');
      Array.from(element.attributes).forEach((attr) => {
        newButton.setAttribute(attr.name, attr.value);
      });
      newButton.innerHTML = element.innerHTML;
      if (!newButton.hasAttribute('type')) {
        newButton.setAttribute('type', 'button');
      }
      element.parentNode?.replaceChild(newButton, element);
      finalElement = newButton;
    }

    // Set or remove navigation action
    if (action && action !== 'none') {
      finalElement.setAttribute('data-sf-action', action);
    } else {
      finalElement.removeAttribute('data-sf-action');
    }

    // Remove link attributes from buttons
    finalElement.removeAttribute('href');
    finalElement.removeAttribute('target');
  }

  return { success: true, html: getHtmlFromDoc(doc) };
}

/**
 * Get the HTML content and setter for a given target.
 * Returns undefined if the target is not valid.
 */
function getHtmlAccessor(
  target: HtmlStepperUpdateTarget,
  formData: HtmlStepperFormData,
):
  | { html: string | undefined; setHtml: (newHtml: string) => void }
  | undefined {
  if (target.type === 'step') {
    const step = formData.steps.find((s) => s.id === target.stepId);
    if (!step) {
      console.warn(`[STEPPER_UPDATER] Step not found: ${target.stepId}`);
      return undefined;
    }
    return {
      html: step.html,
      setHtml: (newHtml: string) => {
        step.html = newHtml;
      },
    };
  }

  if (target.type === 'wrapper') {
    return {
      html: formData.wrapper,
      setHtml: (newHtml: string) => {
        formData.wrapper = newHtml;
      },
    };
  }

  if (target.type === 'finalStep') {
    const finalStep = formData.finalSteps.find(
      (s) => s.id === target.finalStepId,
    );
    if (!finalStep || finalStep.stepType !== 'html') {
      console.warn(
        `[STEPPER_UPDATER] Final step not found or not HTML: ${target.finalStepId}`,
      );
      return undefined;
    }
    return {
      html: finalStep.html,
      setHtml: (newHtml: string) => {
        finalStep.html = newHtml;
      },
    };
  }

  return undefined;
}

/**
 * Apply an HTML element change to a step
 */
function applyHtmlElementChange(
  update: HtmlStepperUpdate,
  formData: HtmlStepperFormData,
): boolean {
  const { target, elementPath, content } = update;

  if (!elementPath) {
    console.warn(
      '[STEPPER_UPDATER] htmlElementChanged: No elementPath provided',
    );
    return false;
  }

  const accessor = getHtmlAccessor(target, formData);
  if (!accessor || !accessor.html) {
    console.warn('[STEPPER_UPDATER] No HTML content found for target');
    return false;
  }

  const result = applyHtmlElementChangeToHtml(
    accessor.html,
    elementPath,
    content as string,
  );

  if (result.success && result.html) {
    accessor.setHtml(result.html);

    // Sync fields if this is a step
    if (target.type === 'step') {
      const step = formData.steps.find((s) => s.id === target.stepId);
      if (step) syncStepFields(step);
    }

    console.log('[STEPPER_UPDATER] htmlElementChanged applied:', {
      target,
      elementPath,
      content,
    });
    return true;
  }

  console.error('[STEPPER_UPDATER] Error applying text change:', result.error);
  return false;
}

/**
 * Apply an HTML action change (button/link settings)
 * Uses the new type/action/href/target structure for stepper actions.
 */
function applyEditHtmlActionChange(
  update: HtmlStepperUpdate,
  formData: HtmlStepperFormData,
): boolean {
  const { target, elementPath, content } = update;

  if (!elementPath) {
    console.warn('[STEPPER_UPDATER] editHtmlAction: No elementPath provided');
    return false;
  }

  const accessor = getHtmlAccessor(target, formData);
  if (!accessor || !accessor.html) return false;

  const actionContent = content as StepperActionElementData;

  console.log('[STEPPER_UPDATER] editHtmlAction:', {
    elementPath,
    ...actionContent,
  });

  const result = applyStepperActionToHtml(accessor.html, elementPath, {
    action: actionContent.action,
    url: actionContent.url,
    openInNewTab: actionContent.openInNewTab,
    tagName: actionContent.tagName,
    text: actionContent.text,
  });

  if (result.success && result.html) {
    accessor.setHtml(result.html);

    // Sync fields if this is a step
    if (target.type === 'step') {
      const step = formData.steps.find((s) => s.id === target.stepId);
      if (step) syncStepFields(step);
    }

    console.log('[STEPPER_UPDATER] editHtmlAction applied successfully');
    return true;
  }

  console.error(
    '[STEPPER_UPDATER] Error applying HTML action change:',
    result.error,
  );
  return false;
}

/**
 * Helper to set or remove a boolean attribute
 */
function setBooleanAttr(
  element: Element,
  attrName: string,
  value: boolean | undefined,
) {
  if (value === undefined) return;
  if (value) {
    element.setAttribute(attrName, 'true');
  } else {
    element.removeAttribute(attrName);
  }
}

/**
 * Helper to set or remove a string attribute
 */
function setStringAttr(
  element: Element,
  attrName: string,
  value: string | undefined,
) {
  if (value === undefined) return;
  if (value) {
    element.setAttribute(attrName, value);
  } else {
    element.removeAttribute(attrName);
  }
}

/**
 * Helper to set or remove a numeric attribute
 */
function setNumericAttr(
  element: Element,
  attrName: string,
  value: number | undefined,
) {
  if (value === undefined) return;
  if (value !== null && !isNaN(value)) {
    element.setAttribute(attrName, String(value));
  } else {
    element.removeAttribute(attrName);
  }
}

interface FieldOptionData {
  value: string;
  label?: string;
}

/**
 * Apply a field settings change
 */
function applyEditFieldChange(
  update: HtmlStepperUpdate,
  formData: HtmlStepperFormData,
): boolean {
  const { target, elementPath, content } = update;

  if (!elementPath || target.type !== 'step') {
    console.warn('[STEPPER_UPDATER] editField: Invalid target or elementPath');
    return false;
  }

  const step = formData.steps.find((s) => s.id === target.stepId);
  if (!step) return false;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(step.html, 'text/html');

    const targetElement = doc.querySelector(elementPath);
    if (!targetElement) {
      console.warn(
        `[STEPPER_UPDATER] Element not found for path: ${elementPath}`,
      );
      return false;
    }

    const {
      id,
      type,
      required,
      protected: protected_,
      placeholder,
      autoAdvance,
      multiple,
      // New validation attributes
      pattern,
      minValue,
      maxValue,
      step: stepValue,
      minDate,
      maxDate,
      min,
      max,
      defaultValue,
      // Options for select/multiChoice
      options,
      // Address field attributes
      restrictCountries,
      placeTypes,
      editableAfterSelection,
      componentConfig,
      allowManualEntry,
      language,
    } = content as Record<string, unknown>;

    // Update basic data-sf-* attributes
    if (id) targetElement.setAttribute('data-sf-field', id as string);
    if (type) targetElement.setAttribute('data-sf-type', type as string);

    setBooleanAttr(targetElement, 'data-sf-required', required as boolean);
    setBooleanAttr(targetElement, 'data-sf-protected', protected_ as boolean);
    setStringAttr(targetElement, 'data-sf-placeholder', placeholder as string);
    setBooleanAttr(
      targetElement,
      'data-sf-auto-advance',
      autoAdvance as boolean,
    );
    setBooleanAttr(targetElement, 'data-sf-multiple', multiple as boolean);

    // New validation attributes
    setStringAttr(targetElement, 'data-sf-pattern', pattern as string);
    setNumericAttr(targetElement, 'data-sf-min-value', minValue as number);
    setNumericAttr(targetElement, 'data-sf-max-value', maxValue as number);
    setNumericAttr(targetElement, 'data-sf-step', stepValue as number);
    setStringAttr(targetElement, 'data-sf-min-date', minDate as string);
    setStringAttr(targetElement, 'data-sf-max-date', maxDate as string);
    setNumericAttr(targetElement, 'data-sf-min', min as number);
    setNumericAttr(targetElement, 'data-sf-max', max as number);
    setStringAttr(
      targetElement,
      'data-sf-default-value',
      defaultValue as string,
    );

    // Address field attributes
    if (type === 'address') {
      // Restrict countries (array → comma-separated string)
      if (restrictCountries !== undefined) {
        if (Array.isArray(restrictCountries) && restrictCountries.length > 0) {
          targetElement.setAttribute(
            'data-sf-restrict-countries',
            (restrictCountries as string[]).join(','),
          );
        } else {
          targetElement.removeAttribute('data-sf-restrict-countries');
        }
      }

      // Place types (array → comma-separated string)
      if (placeTypes !== undefined) {
        if (Array.isArray(placeTypes) && placeTypes.length > 0) {
          targetElement.setAttribute(
            'data-sf-place-types',
            (placeTypes as string[]).join(','),
          );
        } else {
          targetElement.removeAttribute('data-sf-place-types');
        }
      }

      // Boolean options
      setBooleanAttr(
        targetElement,
        'data-sf-editable-after-selection',
        editableAfterSelection as boolean,
      );
      setBooleanAttr(
        targetElement,
        'data-sf-allow-manual-entry',
        allowManualEntry as boolean,
      );

      // Language
      setStringAttr(targetElement, 'data-sf-language', language as string);

      // Component config (set individual attributes)
      if (componentConfig !== undefined) {
        const config = componentConfig as Record<string, string>;
        const componentKeys = [
          'streetNumber',
          'route',
          'secondaryLine',
          'locality',
          'administrativeAreaLevel1',
          'postalCode',
          'country',
        ];

        for (const key of componentKeys) {
          // Convert camelCase to kebab-case for attribute name
          const attrName = `data-sf-component-${key
            .replace(/([A-Z])/g, '-$1')
            .toLowerCase()}`;
          const value = config[key];
          if (value && ['hidden', 'optional', 'required'].includes(value)) {
            targetElement.setAttribute(attrName, value);
          } else {
            targetElement.removeAttribute(attrName);
          }
        }
      }
    }

    // Handle options for select/multiChoice fields
    if (options !== undefined && Array.isArray(options)) {
      const optionData = options as FieldOptionData[];

      // Find all existing option elements (in DOM order)
      const existingOptions = Array.from(
        targetElement.querySelectorAll('[data-sf-option]'),
      );

      // Update existing options by index (supports value editing)
      optionData.forEach((opt, index) => {
        if (index < existingOptions.length) {
          const el = existingOptions[index];
          // Update the option's value attribute
          el.setAttribute('data-sf-option', opt.value);
          // Update label if provided
          if (opt.label) {
            el.setAttribute('data-sf-label', opt.label);
          } else {
            el.removeAttribute('data-sf-label');
          }
        }
        // Note: We don't create new option elements here since that would
        // require duplicating the HTML structure. New options should be
        // handled by regenerating via the AI agent.
      });

      // Remove extra options if the new array is shorter (delete)
      for (let i = optionData.length; i < existingOptions.length; i++) {
        existingOptions[i].remove();
      }

      // Handle reordering: if we have a container, reorder elements
      if (existingOptions.length > 0 && existingOptions[0].parentElement) {
        const optionsContainer = existingOptions[0].parentElement;
        // Get remaining option elements after potential deletions
        const remainingOptions = Array.from(
          targetElement.querySelectorAll('[data-sf-option]'),
        );
        // Reorder by appending in the new order
        remainingOptions.forEach((el) => {
          optionsContainer.appendChild(el);
        });
      }
    }

    step.html = doc.body.innerHTML;

    // Re-extract fields after HTML modification
    syncStepFields(step);

    console.log('[STEPPER_UPDATER] editField applied:', {
      target,
      elementPath,
      content,
    });
    return true;
  } catch (error) {
    console.error('[STEPPER_UPDATER] Error applying field change:', error);
    return false;
  }
}

/**
 * Apply image settings change (objectFit, objectPosition)
 */
function applyEditImageSettingsChange(
  update: HtmlStepperUpdate,
  formData: HtmlStepperFormData,
): boolean {
  const { target, elementPath, content } = update;

  if (!elementPath) {
    console.warn(
      '[STEPPER_UPDATER] editImageSettings: No elementPath provided',
    );
    return false;
  }

  const accessor = getHtmlAccessor(target, formData);
  if (!accessor || !accessor.html) return false;

  const settings = content as AssetSettingsContent;

  console.log('[STEPPER_UPDATER] editImageSettings:', {
    target,
    elementPath,
    ...settings,
  });

  // Use applyAssetSettingsToHtml for img/video setting support
  const result = applyAssetSettingsToHtml(accessor.html, elementPath, settings);

  if (result.success && result.html) {
    accessor.setHtml(result.html);

    // Sync fields if this is a step
    if (target.type === 'step') {
      const step = formData.steps.find((s) => s.id === target.stepId);
      if (step) syncStepFields(step);
    }

    console.log('[STEPPER_UPDATER] editImageSettings applied successfully');
    return true;
  }

  console.error(
    '[STEPPER_UPDATER] Error applying image settings change:',
    result.error,
  );
  return false;
}

/**
 * Apply image selection change (update src attribute)
 */
function applySelectImageChange(
  update: HtmlStepperUpdate,
  formData: HtmlStepperFormData,
): boolean {
  const { target, elementPath, content } = update;

  if (!elementPath) {
    console.warn('[STEPPER_UPDATER] selectImage: No elementPath provided');
    return false;
  }

  const accessor = getHtmlAccessor(target, formData);
  if (!accessor || !accessor.html) return false;

  console.log('[STEPPER_UPDATER] selectImage:', {
    target,
    elementPath,
    content,
  });

  // Use applySelectAssetToHtml for img/video/svg switching support
  const result = applySelectAssetToHtml(
    accessor.html,
    elementPath,
    content as SelectAssetData,
  );

  if (result.success && result.html) {
    accessor.setHtml(result.html);

    // Sync fields if this is a step
    if (target.type === 'step') {
      const step = formData.steps.find((s) => s.id === target.stepId);
      if (step) syncStepFields(step);
    }

    console.log('[STEPPER_UPDATER] selectImage applied successfully');
    return true;
  }

  console.error(
    '[STEPPER_UPDATER] Error applying image selection:',
    result.error,
  );
  return false;
}

/**
 * Apply thumbnail selection to a video element.
 * Sets data-thumbnail-url, data-thumbnail-alt, and poster attributes.
 */
function applySelectThumbnailChange(
  update: HtmlStepperUpdate,
  formData: HtmlStepperFormData,
): boolean {
  const { target, elementPath, content } = update;

  if (!elementPath) {
    console.warn('[STEPPER_UPDATER] selectThumbnail: No elementPath provided');
    return false;
  }

  const accessor = getHtmlAccessor(target, formData);
  if (!accessor || !accessor.html) return false;

  console.log('[STEPPER_UPDATER] selectThumbnail:', {
    target,
    elementPath,
    content,
  });

  const result = applySelectThumbnailToHtml(
    accessor.html,
    elementPath,
    content as SelectThumbnailData,
  );

  if (result.success && result.html) {
    accessor.setHtml(result.html);

    console.log('[STEPPER_UPDATER] selectThumbnail applied successfully');
    return true;
  }

  console.error(
    '[STEPPER_UPDATER] Error applying thumbnail selection:',
    result.error,
  );
  return false;
}

/**
 * Apply a revert change (undo)
 * The content contains the previous form state to restore.
 */
function applyRevertChange(
  update: HtmlStepperUpdate,
  formData: HtmlStepperFormData,
): boolean {
  const previousState = update.content as HtmlStepperFormData;
  if (!previousState) {
    console.warn('[STEPPER_UPDATER] revertChange: No previous state provided');
    return false;
  }

  // Replace all properties with the previous state
  Object.keys(formData).forEach((key) => {
    delete (formData as any)[key];
  });
  Object.assign(formData, previousState);

  // Sync fields for all steps
  formData.steps.forEach((step) => syncStepFields(step));

  console.log('[STEPPER_UPDATER] revertChange applied');
  return true;
}

/**
 * Apply a full form data replacement (e.g., from agent updates)
 */
function applyReplaceFormData(
  update: HtmlStepperUpdate,
  formData: HtmlStepperFormData,
): boolean {
  const newFormData = update.content as HtmlStepperFormData;
  if (!newFormData) {
    console.warn('[STEPPER_UPDATER] replaceFormData: No content provided');
    return false;
  }

  // Replace all properties
  Object.keys(formData).forEach((key) => {
    delete (formData as any)[key];
  });
  Object.assign(formData, newFormData);

  // Sync fields for all steps
  formData.steps.forEach((step) => syncStepFields(step));

  console.log('[STEPPER_UPDATER] replaceFormData applied');
  return true;
}

/**
 * Main dispatcher - applies an update to the form data
 */
export function applyStepperChange(
  update: HtmlStepperUpdate,
  formData: HtmlStepperFormData,
): boolean {
  switch (update.type) {
    case 'htmlElementChanged':
      return applyHtmlElementChange(update, formData);

    case 'editHtmlAction':
      return applyEditHtmlActionChange(update, formData);

    case 'editField':
      return applyEditFieldChange(update, formData);

    case 'editImageSettings':
      return applyEditImageSettingsChange(update, formData);

    case 'selectImage':
      return applySelectImageChange(update, formData);

    case 'selectThumbnail':
      return applySelectThumbnailChange(update, formData);

    case 'replaceFormData':
      return applyReplaceFormData(update, formData);

    case 'revertChange':
      return applyRevertChange(update, formData);

    default:
      console.warn(
        `[STEPPER_UPDATER] Unknown update type: ${(update as any).type}`,
      );
      return false;
  }
}

/**
 * Apply multiple updates to form data synchronously
 */
export function applyStepperChangesSync(
  formData: HtmlStepperFormData,
  updates: HtmlStepperUpdate[],
): HtmlStepperFormData {
  updates.forEach((update) => applyStepperChange(update, formData));
  return formData;
}
